import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { uiBus } from 'shared/api/uiBus';

const PROVIDERS = [
  { value: 'TOPLUSMS', label: 'TopluSMS' },
  { value: 'VATANSMS', label: 'VatanSMS (vatansms.net)' },
];

export function SmsApiSettingsModal({ open, onClose, smsCompany, onSave }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState(false);

  // confirm
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);

  // ✅ ilk açılışta düzenleme kapalı
  const [editing, setEditing] = useState(false);

  const [form, setForm] = useState({
    moduleEnabled: false,

    smsMode: 'COMPANY_PROVIDER',
    smsProvider: 'TOPLUSMS',

    // TopluSMS
    topluApiKey: '',
    topluSender: '',
    topluMessageType: 'normal',
    topluMessageContentType: 'bilgi',
    topluAddCancelLink: true,

    // VatanSMS (API_ID + API_KEY + SENDER)
    vatanApiId: '',
    vatanApiKey: '',
    vatanSender: '',
  });
  const activeProvider = smsCompany?.smsProvider || form.smsProvider;
  const activeMode = smsCompany?.smsMode || form.smsMode;

  const isTopluActive = activeProvider === 'TOPLUSMS';
  const isVatanActive = activeProvider === 'VATANSMS';

  // Backend’den gelen provider/mode varsa onları esas al
  useEffect(() => {
    if (!open) return;

    setOk(false);
    setErr('');
    setSaving(false);

    // ✅ her açılışta read-only başla
    setEditing(false);

    // ✅ confirm kapalı başla
    setConfirmOpen(false);
    setConfirmBusy(false);

    const provider = smsCompany?.smsProvider || 'TOPLUSMS';
    const mode = smsCompany?.smsMode || 'COMPANY_PROVIDER';

    setForm((prev) => ({
      ...prev,
      moduleEnabled: Boolean(smsCompany?.moduleEnabled),

      smsMode: mode,
      smsProvider: provider,

      // ✅ key'ler backend'den dönmüyor: boş başlat
      topluApiKey: '',
      topluSender: smsCompany?.topluSender || '',
      topluAddCancelLink:
        typeof smsCompany?.topluAddCancelLink === 'boolean' ? smsCompany.topluAddCancelLink : true,

      // VATAN: api_id + sender dönebilir, api_key dönmez
      vatanApiId: smsCompany?.vatanApiId || '',
      vatanApiKey: '',
      vatanSender: smsCompany?.vatanSender || '',
    }));
  }, [open, smsCompany]);

  const handleClose = useCallback(() => {
    if (saving) return;
    onClose?.();
  }, [saving, onClose]);

  const isToplu = form.smsProvider === 'TOPLUSMS';
  const isVatan = form.smsProvider === 'VATANSMS';

  const dialogTitle = useMemo(() => {
    if (isVatan) return 'API Ayarları';
    return 'TopluSMS API Ayarları';
  }, [isVatan]);

  const canEdit = open && !saving;

  const handleStartEdit = useCallback(() => {
    if (!canEdit) return;
    setOk(false);
    setErr('');
    setEditing(true);
  }, [canEdit]);

  const handleCancelEdit = useCallback(() => {
    if (saving) return;

    const provider = smsCompany?.smsProvider || 'TOPLUSMS';
    const mode = smsCompany?.smsMode || 'COMPANY_PROVIDER';

    setOk(false);
    setErr('');
    setEditing(false);

    setForm((prev) => ({
      ...prev,
      moduleEnabled: Boolean(smsCompany?.moduleEnabled),
      smsMode: mode,
      smsProvider: provider,

      topluApiKey: '',
      topluSender: smsCompany?.topluSender || '',
      topluAddCancelLink:
        typeof smsCompany?.topluAddCancelLink === 'boolean' ? smsCompany.topluAddCancelLink : true,

      vatanApiId: smsCompany?.vatanApiId || '',
      vatanApiKey: '',
      vatanSender: smsCompany?.vatanSender || '',
    }));
  }, [saving, smsCompany]);

  // ✅ boolean döner
  const handleSave = useCallback(async () => {
    if (!editing) return false;

    setSaving(true);
    setErr('');
    setOk(false);

    try {
      const hasTopluKeyAlready = Boolean(smsCompany?.hasTopluApiKey);
      const hasVatanKeyAlready = Boolean(smsCompany?.hasVatanApiKey);

      if (isToplu) {
        if (!form.topluSender?.trim()) throw new Error('TopluSMS Sender (başlık) boş olamaz.');
        if (!hasTopluKeyAlready && !form.topluApiKey?.trim()) {
          throw new Error('TopluSMS API Key boş olamaz (ilk kurulum).');
        }
      }

      if (isVatan) {
        if (!form.vatanApiId?.trim()) throw new Error('VatanSMS API ID boş olamaz.');
        if (!form.vatanSender?.trim()) throw new Error('VatanSMS Sender (başlık) boş olamaz.');
        if (!hasVatanKeyAlready && !form.vatanApiKey?.trim()) {
          throw new Error('VatanSMS API Key/Şifre boş olamaz (ilk kurulum).');
        }
      }

      const payload = {
        moduleEnabled: form.moduleEnabled,
        smsMode: 'COMPANY_PROVIDER',
        smsProvider: form.smsProvider,
      };

      if (isToplu) {
        Object.assign(payload, {
          topluApiKey: form.topluApiKey, // boş gelebilir -> backend eskiyi korumalı
          topluSender: form.topluSender,
          topluAddCancelLink: form.topluAddCancelLink,
          // istersek şunları da ekleriz:
          // topluMessageType: form.topluMessageType,
          // topluMessageContentType: form.topluMessageContentType,
        });
      }

      if (isVatan) {
        Object.assign(payload, {
          vatanApiId: form.vatanApiId,
          vatanApiKey: form.vatanApiKey, // boş gelebilir -> backend eskiyi korumalı
          vatanSender: form.vatanSender,
          // istersek şunları da ekleriz:
          // vatanMessageType: form.vatanMessageType,
          // vatanMessageContentType: form.vatanMessageContentType,
        });
      }

      await onSave?.(payload);

      setOk(true);
      setEditing(false);
      return true;
    } catch (e) {
      setErr(e?.message || 'Kaydedilemedi.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [editing, form, onSave, smsCompany, isToplu, isVatan]);

  const readOnly = !editing;

  // confirm summary
  const confirmProviderLabel = isVatan ? 'VatanSMS' : 'TopluSMS';
  const confirmSender = isVatan ? form.vatanSender : form.topluSender;

  const apiKeyStatusText = useMemo(() => {
    if (isVatan) {
      const already = Boolean(smsCompany?.hasVatanApiKey);
      if (!already) return 'Yeni kaydedilecek';
      return form.vatanApiKey?.trim() ? 'Güncellenecek' : 'Kayıtlı (değişmeyecek)';
    }
    const already = Boolean(smsCompany?.hasTopluApiKey);
    if (!already) return 'Yeni kaydedilecek';
    return form.topluApiKey?.trim() ? 'Güncellenecek' : 'Kayıtlı (değişmeyecek)';
  }, [isVatan, smsCompany, form.vatanApiKey, form.topluApiKey]);

  const apiIdStatusText = useMemo(() => {
    if (!isVatan) return null;
    return form.vatanApiId?.trim() ? form.vatanApiId.trim() : '—';
  }, [isVatan, form.vatanApiId]);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox display="flex" alignItems="center" gap={1}>
              <Icon>{isVatan ? 'sms' : 'settings'}</Icon>
              <span>{dialogTitle}</span>
            </MDBox>

            <MDBox display="flex" alignItems="center" gap={1}>
              {readOnly ? (
                <Tooltip title="Düzenle">
                  <span>
                    <IconButton onClick={handleStartEdit} disabled={!canEdit}>
                      <Icon>edit</Icon>
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Düzenlemeyi iptal et">
                  <span>
                    <IconButton onClick={handleCancelEdit} disabled={saving}>
                      <Icon>close</Icon>
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </MDBox>
          </MDBox>
        </DialogTitle>

        <DialogContent dividers>
          {err ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {err}
            </Alert>
          ) : null}

          {ok ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Kaydedildi.
            </Alert>
          ) : null}

          <Alert severity="info" sx={{ mb: 2 }}>
            Ayarlar varsayılan olarak görüntüleme modunda açılır. Düzenlemek için sağ üstteki{' '}
            <b>Düzenle</b> ikonuna tıklayın.
          </Alert>

          {/* Provider seçimi */}
          <TextField
            select
            fullWidth
            label="SMS Sağlayıcısı"
            value={form.smsProvider}
            disabled={readOnly}
            onChange={(e) => setForm((p) => ({ ...p, smsProvider: e.target.value }))}
          >
            {PROVIDERS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>

          <Divider sx={{ my: 2 }} />

          {/* ===================== TOPLUSMS ===================== */}
          {isToplu ? (
            <>
              <TextField
                fullWidth
                label="TopluSMS API Key"
                value={
                  readOnly
                    ? smsCompany?.hasTopluApiKey
                      ? '•••••••• (Kayıtlı)'
                      : ''
                    : form.topluApiKey
                }
                disabled={readOnly}
                onChange={(e) => setForm((p) => ({ ...p, topluApiKey: e.target.value }))}
                placeholder={smsCompany?.hasTopluApiKey ? 'Kayıtlı' : 'API Key'}
                sx={{ mt: 1 }}
                helperText={
                  readOnly
                    ? 'Güvenlik nedeniyle API Key gösterilmez.'
                    : smsCompany?.hasTopluApiKey
                    ? 'Boş bırakırsan mevcut key korunur (backend eskiyi korumalı).'
                    : 'İlk kurulum için zorunlu.'
                }
              />

              <TextField
                fullWidth
                label='Sender (JSON "sender")'
                value={form.topluSender}
                disabled={readOnly}
                onChange={(e) => setForm((p) => ({ ...p, topluSender: e.target.value }))}
                sx={{ mt: 2 }}
              />
            </>
          ) : null}

          {/* ===================== VATANSMS ===================== */}
          {isVatan ? (
            <>
              <TextField
                fullWidth
                label="VatanSMS API ID"
                value={form.vatanApiId}
                disabled={readOnly}
                onChange={(e) => setForm((p) => ({ ...p, vatanApiId: e.target.value }))}
                sx={{ mt: 1 }}
                helperText="VatanSMS panelinizde verilen API ID."
              />

              <TextField
                fullWidth
                label="VatanSMS API Key / Şifre"
                value={
                  readOnly
                    ? smsCompany?.hasVatanApiKey
                      ? '•••••••• (Kayıtlı)'
                      : ''
                    : form.vatanApiKey
                }
                disabled={readOnly}
                onChange={(e) => setForm((p) => ({ ...p, vatanApiKey: e.target.value }))}
                placeholder={smsCompany?.hasVatanApiKey ? 'Kayıtlı' : 'API Key/Şifre'}
                sx={{ mt: 2 }}
                helperText={
                  readOnly
                    ? 'Güvenlik nedeniyle API Key gösterilmez.'
                    : smsCompany?.hasVatanApiKey
                    ? 'Boş bırakırsan mevcut key korunur (backend eskiyi korumalı).'
                    : 'İlk kurulum için zorunlu.'
                }
              />

              <TextField
                fullWidth
                label="Sender (Başlık)"
                value={form.vatanSender}
                disabled={readOnly}
                onChange={(e) => setForm((p) => ({ ...p, vatanSender: e.target.value }))}
                sx={{ mt: 2 }}
                helperText="Tanımlı gönderici başlığı."
              />
            </>
          ) : null}
          {/* ===================== PROVIDER CONNECTION STATUS ===================== */}

          {readOnly && activeMode === 'COMPANY_PROVIDER' && isTopluActive ? (
            smsCompany?.topluUserInfoOk ? (
              <Alert severity="success" sx={{ mt: 2 }}>
                TopluSMS bağlantısı doğrulandı. Kredi:{' '}
                {smsCompany?.topluUserInfoRaw?.data?.credit ?? '—'}
                {' · '}Şirket: {smsCompany?.topluUserInfoRaw?.data?.company_name ?? '—'}
                {' · '}Kullanıcı: {smsCompany?.topluUserInfoRaw?.data?.username ?? '—'}
              </Alert>
            ) : smsCompany?.topluUserInfoError ? (
              <Alert severity="warning" sx={{ mt: 2 }}>
                TopluSMS doğrulama hatası: {smsCompany.topluUserInfoError}
              </Alert>
            ) : null
          ) : null}

          {readOnly && activeMode === 'COMPANY_PROVIDER' && isVatanActive ? (
            smsCompany?.vatanUserInfoOk ? (
              <Alert severity="success" sx={{ mt: 2 }}>
                VatanSMS bağlantısı doğrulandı. Kredi:{' '}
                {smsCompany?.vatanUserInfoRaw?.data?.credit ?? '—'}
                {' · '}Şirket: {smsCompany?.vatanUserInfoRaw?.data?.company_name ?? '—'}
                {' · '}Kullanıcı: {smsCompany?.vatanUserInfoRaw?.data?.username ?? '—'}
              </Alert>
            ) : smsCompany?.vatanUserInfoError ? (
              <Alert severity="warning" sx={{ mt: 2 }}>
                VatanSMS doğrulama hatası: {smsCompany.vatanUserInfoError}
              </Alert>
            ) : null
          ) : null}
        </DialogContent>

        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={handleClose} disabled={saving}>
            Kapat
          </MDButton>

          <MDButton
            variant="gradient"
            color="info"
            disabled={saving || readOnly}
            onClick={() => setConfirmOpen(true)}
            startIcon={saving ? <CircularProgress size={16} /> : <Icon>save</Icon>}
          >
            Kaydet
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ✅ Confirm Save */}
      <Dialog
        open={confirmOpen}
        onClose={() => {
          if (confirmBusy) return;
          setConfirmOpen(false);
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ fontSize: 18, color: 'warning.main' }}>warning</Icon>
            <span>Emin misin?</span>
          </MDBox>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            API bilgilerini kaydetmek üzeresin. Yanlış bilgi girersen SMS gönderimi çalışmayabilir.
          </Alert>

          <MDBox sx={{ display: 'grid', gap: 0.75 }}>
            <MDBox sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <span>Sağlayıcı</span>
              <b>{confirmProviderLabel}</b>
            </MDBox>

            {isVatan ? (
              <MDBox sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <span>API ID</span>
                <b>{apiIdStatusText}</b>
              </MDBox>
            ) : null}

            <MDBox sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <span>Sender</span>
              <b>{confirmSender || '—'}</b>
            </MDBox>

            <MDBox sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <span>API Key</span>
              <b>{apiKeyStatusText}</b>
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            variant="text"
            color="secondary"
            disabled={confirmBusy}
            onClick={() => setConfirmOpen(false)}
          >
            Vazgeç
          </MDButton>

          <MDButton
            variant="gradient"
            color="info"
            disabled={confirmBusy}
            onClick={async () => {
              setConfirmBusy(true);
              try {
                const saved = await handleSave();
                if (saved) {
                  setConfirmOpen(false);
                  uiBus.emit('toast', { type: 'success', message: 'API bilgileri kayıt edildi.' });
                } else {
                  uiBus.emit('toast', { type: 'error', message: 'Kaydedilemedi.' });
                }
              } finally {
                setConfirmBusy(false);
              }
            }}
            startIcon={confirmBusy ? <CircularProgress size={16} /> : <Icon>check</Icon>}
          >
            Evet, Kaydet
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
